<template>
  <div class="app-wrapper">
    <RouterView />
    <AuthModal />
    <RestaurantClosedModal ref="bonusesErrorModal" @close="terminalStore.bonusesUnavailableShow = false" />
    <ContinueModal :show="showContinueModal" @reset-timer="resetInactivityTimer" @close="showContinueModal = false" />
    <img v-if="showBuildIcon" class="build-icon" :src="buildIconUrl" alt="">
    <OfflineIndicator />
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import RestaurantClosedModal from '@/components/cartView/restaurantClosedModal.vue';
import AuthModal from '@/components/layouts/AuthModal/';
import ContinueModal from '@/components/layouts/ContinueModal/';
import OfflineIndicator from '@/components/UI/OfflineIndicator.vue';

import { usePaymentStore, useTerminalStore } from '@/stores';
import { productProfile } from '@/config/productProfile';
import type { TerminalData } from '@/types';

const route = useRoute();
const router = useRouter();
const terminalStore = useTerminalStore();
const paymentStore = usePaymentStore();
const { t } = useI18n();
const bonusesErrorModal = ref<InstanceType<typeof RestaurantClosedModal> | null>(null);
watch(() => terminalStore.bonusesUnavailableShow, (show) => {
    if (show) {
        bonusesErrorModal.value?.showModal(t('bonusesUnavailable.message'), t('bonusesUnavailable.title'));
    }
});

const inactivityTimer = ref<number | null>(null);
const showContinueModal = ref(false);

const buildIconUrl = computed(() => import.meta.env.VITE_BUILD_ICON_URL || '');

const showBuildIcon = computed(() => productProfile.buildIcon
    && import.meta.env.VITE_SHOW_BUILD_ICON === 'true'
    && Boolean(buildIconUrl.value));

function setTerminal(data: TerminalData) {
    terminalStore.setTerminal(data);
}

function isLocalhost() {
    return (
        location.hostname === 'localhost' ||
        location.hostname === '127.0.0.1'
    );
}

function init() {
    const cafe_slug = localStorage.getItem('cafe_slug');
    const shop_id = localStorage.getItem('shop_id');
    const terminal_id = localStorage.getItem('terminal_id');
    let terminal_type = localStorage.getItem('terminal_type');

    if (!terminal_type) {
        localStorage.setItem('terminal_type', 't-bank');
        terminal_type = 't-bank';
    }

    const isPreview = import.meta.env.DEV && ['/preview/order-error', '/preview/payment-loading'].includes(window.location.pathname);
    if ((!cafe_slug || !shop_id || !terminal_id) && !window.location.pathname.startsWith('/not-working') && !window.location.pathname.startsWith('/service') && !isPreview) {
        router.push('/settings');
    } else {
        const terminalData: TerminalData = { cafe_slug, shop_id, terminal_id, terminal_type };

        console.log(`
        --------------------------------------------
            Инициализация успешна!
            Код точки продажи: ${cafe_slug}
            ID точки продажи: ${shop_id}
            Device ID: ${terminal_id}
            Тип терминала: ${terminal_type}
        --------------------------------------------
        `);

        setTerminal(terminalData);
    }
}

function resetInactivityTimer() {
    if (paymentStore.isCreatingOrder) {
        clearTimeout(inactivityTimer.value ?? undefined);
        showContinueModal.value = false;
        return;
    }

    if (productProfile.kind === 'tablets') {
        clearTimeout(inactivityTimer.value ?? undefined);
        showContinueModal.value = false;
        if (route.meta.timer) {
            inactivityTimer.value = window.setTimeout(() => {
                showContinueModal.value = true;
            }, 30000);
        }
        return;
    }

    // На localhost таймер не работает
    if (isLocalhost()) {
        clearTimeout(inactivityTimer.value ?? undefined);
        showContinueModal.value = false;
        return;
    }

    // Игнорируем страницы без таймера
    if (!route.meta.timer) {
        clearTimeout(inactivityTimer.value ?? undefined);
        showContinueModal.value = false;
        return;
    }

    clearTimeout(inactivityTimer.value ?? undefined);
    inactivityTimer.value = window.setTimeout(() => {
        showContinueModal.value = true;
    }, 25000);
}

watch([() => route.path, () => paymentStore.isCreatingOrder], () => {
    resetInactivityTimer();
});

onMounted(() => {
    init();
    ['click', 'mousemove', 'keydown', 'touchstart'].forEach(event => {
        window.addEventListener(event, resetInactivityTimer);
    });
    resetInactivityTimer();
});

onBeforeUnmount(() => {
    ['click', 'mousemove', 'keydown', 'touchstart'].forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
    });
    clearTimeout(inactivityTimer.value ?? undefined);
});
</script>
<style scoped lang="scss">
.app-wrapper {
	width: 100dvw;
	height: 100dvh;
	overflow: hidden;
}

.build-icon {
	position: fixed;
	top: max(12px, env(safe-area-inset-top));
	right: max(12px, env(safe-area-inset-right));
	z-index: 10000;
	width: 44px;
	height: 44px;
	object-fit: contain;
	border-radius: 10px;
	pointer-events: none;
	user-select: none;
}
</style>
